import { useState } from 'react'

export interface CalendarEvent {
  id: string
  date: string // 'YYYY-MM-DD' in the viewer's LOCAL time, matching how times are displayed everywhere else
  label: string
  color: string
  onClick?: () => void
}

interface MonthGridProps {
  events: CalendarEvent[]
  selectedDate: string | null
  onSelectDate: (date: string | null) => void
}

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function toDateKey(d: Date) {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/**
 * A plain CSS month-view calendar grid — no external calendar library, matching this
 * project's minimal-dependency convention. Events are plotted by their LOCAL calendar-day
 * key (toDateKey), consistent with how every lesson time elsewhere in the app is already
 * shown in the viewer's own local time, never UTC. Clicking a day selects/deselects it
 * (the caller decides what "selected" does, e.g. filtering a list below the grid);
 * clicking an event chip calls its own onClick directly without changing the selection.
 */
export function MonthGrid({ events, selectedDate, onSelectDate }: MonthGridProps) {
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), 1)
  })

  const todayKey = toDateKey(new Date())

  const eventsByDate = new Map<string, CalendarEvent[]>()
  for (const ev of events) {
    const list = eventsByDate.get(ev.date) ?? []
    list.push(ev)
    eventsByDate.set(ev.date, list)
  }

  const year = visibleMonth.getFullYear()
  const month = visibleMonth.getMonth()
  const firstOfMonth = new Date(year, month, 1)
  const startWeekday = firstOfMonth.getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const gridStart = new Date(year, month, 1 - startWeekday)

  const cells: Date[] = []
  for (let i = 0; i < 42; i++) {
    cells.push(new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i))
  }
  // Trim trailing all-empty rows beyond the 5th if the month fits in 5 rows.
  const rowsNeeded = Math.ceil((startWeekday + daysInMonth) / 7)
  const visibleCells = cells.slice(0, rowsNeeded * 7)

  const monthLabel = visibleMonth.toLocaleDateString('en-CA', { month: 'long', year: 'numeric' })

  return (
    <div style={{
      background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14, overflow: 'hidden', marginBottom: 18,
      height: 'calc(100vh - 170px)', minHeight: 460, display: 'flex', flexDirection: 'column',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
        padding: '16px 20px', background: 'linear-gradient(155deg, #0F7C6C, #0B5D51)',
      }}>
        <button
          onClick={() => setVisibleMonth(new Date(year, month - 1, 1))}
          style={{
            width: 30, height: 30, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', border: 'none',
            cursor: 'pointer', font: '600 16px Inter, sans-serif', color: '#fff', lineHeight: 1,
          }}
        >
          ‹
        </button>
        <div style={{ font: '600 17px Lora, serif', color: '#fff' }}>{monthLabel}</div>
        <button
          onClick={() => setVisibleMonth(new Date(year, month + 1, 1))}
          style={{
            width: 30, height: 30, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', border: 'none',
            cursor: 'pointer', font: '600 16px Inter, sans-serif', color: '#fff', lineHeight: 1,
          }}
        >
          ›
        </button>
      </div>

      <div style={{ padding: '18px 20px 0' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 8, flexShrink: 0 }}>
          {WEEKDAY_SHORT.map(d => (
            <div key={d} style={{ textAlign: 'center', font: '700 11px Inter, sans-serif', color: '#0F7C6C', textTransform: 'uppercase', padding: '4px 0' }}>
              {d}
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridAutoRows: '1fr', gap: 4, flex: 1, minHeight: 0, padding: '0 20px 18px' }}>
        {visibleCells.map(cell => {
          const key = toDateKey(cell)
          const inMonth = cell.getMonth() === month
          const isToday = key === todayKey
          const isSelected = key === selectedDate
          const dayEvents = eventsByDate.get(key) ?? []
          return (
            <div
              key={key}
              onClick={() => onSelectDate(isSelected ? null : key)}
              style={{
                borderRadius: 8, padding: '6px 6px', cursor: 'pointer', overflow: 'hidden',
                background: isSelected ? 'rgba(15,124,108,0.1)' : 'transparent',
                border: isSelected ? '1px solid #0F7C6C' : '1px solid rgba(27,42,74,0.06)',
                opacity: inMonth ? 1 : 0.35, transition: 'background-color 0.15s, border-color 0.15s',
              }}
              onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(15,124,108,0.05)' }}
              onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent' }}
            >
              <div style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: isToday ? 22 : undefined, height: isToday ? 22 : undefined,
                borderRadius: '50%', background: isToday ? 'linear-gradient(155deg, #0F7C6C, #0B5D51)' : 'transparent',
                font: isToday ? '700 11.5px Inter, sans-serif' : '500 12px Inter, sans-serif',
                color: isToday ? '#fff' : '#1B2A4A', marginBottom: 3,
              }}>
                {cell.getDate()}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {dayEvents.slice(0, 4).map(ev => (
                  <div
                    key={ev.id}
                    onClick={e => { e.stopPropagation(); ev.onClick?.() }}
                    style={{
                      font: '500 10px Inter, sans-serif', color: '#fff', background: ev.color,
                      borderRadius: 4, padding: '1px 4px', overflow: 'hidden', textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap', cursor: ev.onClick ? 'pointer' : 'default',
                    }}
                  >
                    {ev.label}
                  </div>
                ))}
                {dayEvents.length > 4 && (
                  <div style={{ font: '500 10px Inter, sans-serif', color: 'rgba(27,42,74,0.45)' }}>
                    +{dayEvents.length - 4} more
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
